import { zodResolver as baseZodResolver } from '@hookform/resolvers/zod'
import type { FieldValues, Resolver } from 'react-hook-form'
import type { z } from 'zod'

/**
 * Typed wrapper around `@hookform/resolvers`' `zodResolver`.
 *
 * `@hookform/resolvers` (through 5.4.0) ships `.d.ts` compiled against an older
 * zod-4 minor, so passing a zod 4.4.x schema trips a spurious overload error
 * ("`_zod.version.minor` 4 is not assignable to 0") even though the RUNTIME is
 * fully compatible. This wrapper casts past that version-literal clash.
 *
 * The resolved form-values type (`TFieldValues`) is inferred from the call
 * site's `useForm<T>()` generic via contextual typing, so each form keeps its
 * exact typing. Import `zodResolver` from `@/lib/zod-resolver` instead of
 * `@hookform/resolvers/zod` at every form call site.
 */
export function zodResolver<
  TFieldValues extends FieldValues = FieldValues,
  TContext = unknown,
  TTransformedValues = TFieldValues,
>(
  schema: z.ZodType,
  ...rest: unknown[]
): Resolver<TFieldValues, TContext, TTransformedValues> {
  return (
    baseZodResolver as unknown as (
      ...args: unknown[]
    ) => Resolver<TFieldValues, TContext, TTransformedValues>
  )(schema, ...rest)
}
